import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaMap, FaCog } from 'react-icons/fa';

const StyleSelectorContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  overflow: visible;
`;

const StyleButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  transition: all 0.2s;
  border-radius: 8px;
  
  &:hover {
    background: #f5f5f5;
    border-color: #bbb;
  }
  
  &:active {
    background: #e9e9e9;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 200px;
  overflow: hidden;
  margin-bottom: 8px;
  border: 1px solid #e0e0e0;
  animation: slideUp 0.2s ease-out;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyleOption = styled.button`
  background: white;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  width: 100%;
  text-align: left;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:active {
    background: #e9e9e9;
  }
`;

const StylePreview = styled.div`
  width: 24px;
  height: 16px;
  border-radius: 2px;
  background: ${props => props.color};
  border: 1px solid #ddd;
`;

const MapStyleSelector = ({ currentStyle, onStyleChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const mapStyles = [
    {
      id: 'light-v11',
      name: 'Light',
      url: 'mapbox://styles/mapbox/light-v11',
      preview: '#f8f9fa'
    },
    {
      id: 'dark-v11',
      name: 'Dark',
      url: 'mapbox://styles/mapbox/dark-v11',
      preview: '#242424'
    },
    {
      id: 'streets-v12',
      name: 'Streets',
      url: 'mapbox://styles/mapbox/streets-v12',
      preview: '#ffffff'
    },
    {
      id: 'outdoors-custom',
      name: 'Outdoors',
      url: 'mapbox://styles/parcelhunter/cmdj7drxm006a01r13k7tayfn',
      preview: '#f0f8ff'
    },
    {
      id: 'satellite-v9',
      name: 'Satellite',
      url: 'mapbox://styles/mapbox/satellite-v9',
      preview: '#2d5a27'
    },
    {
      id: 'satellite-streets-v12',
      name: 'Satellite Streets',
      url: 'mapbox://styles/mapbox/satellite-streets-v12',
      preview: '#2d5a27'
    },
    {
      id: 'navigation-day-v1',
      name: 'Navigation Day',
      url: 'mapbox://styles/mapbox/navigation-day-v1',
      preview: '#ffffff'
    },
    {
      id: 'navigation-night-v1',
      name: 'Navigation Night',
      url: 'mapbox://styles/mapbox/navigation-night-v1',
      preview: '#242424'
    }
  ];

  const currentStyleData = mapStyles.find(style => style.url === currentStyle) || mapStyles[0];

  const handleStyleSelect = (style, e) => {
    e.stopPropagation();
    onStyleChange(style.url);
    setIsOpen(false);
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <StyleSelectorContainer ref={dropdownRef}>
      <StyleButton onClick={toggleDropdown}>
        <FaMap />
        {currentStyleData.name}
        <FaCog style={{ marginLeft: 'auto' }} />
      </StyleButton>
      
      {isOpen && (
        <DropdownContainer>
          {mapStyles.map((style) => (
            <StyleOption
              key={style.id}
              onClick={(e) => handleStyleSelect(style, e)}
              style={{
                backgroundColor: currentStyle === style.url ? '#e3f2fd' : 'white'
              }}
            >
              <StylePreview color={style.preview} />
              {style.name}
            </StyleOption>
          ))}
        </DropdownContainer>
      )}
    </StyleSelectorContainer>
  );
};

export default MapStyleSelector; 